// src/pages/dashboards/TeacherDashboard.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  BookOpenIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  BriefcaseIcon,
  ArrowRightIcon,
  ClockIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../api/client';
import toast from 'react-hot-toast';
import { FireLoader } from '../../components/Loader';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { socket } from '../../services/socket';

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teacher, setTeacher] = useState(null);
  const [classes, setClasses] = useState([]);
  const [studentCount, setStudentCount] = useState(0);
  const [attendanceStats, setAttendanceStats] = useState({ present: 0, absent: 0, total: 0 });
  const [recentActivities, setRecentActivities] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [chartLoading, setChartLoading] = useState(true);
  const [classTeacherId, setClassTeacherId] = useState(null);
  const [currentTermStats, setCurrentTermStats] = useState({
    name: 'Current Term',
    percentage: 0,
    attendedSessions: 0,
    possibleSessions: 0,
  });

  const fetchTeacherDataRef = useRef();

  const fetchTeacherData = useCallback(async (options = { background: false }) => {
    try {
      if (!options.background) setLoading(true);

      const teacherRes = await apiClient.get('/teachers/me');
      const teacherData = teacherRes.data?.data ?? teacherRes.data;

      if (!teacherData?.id) {
        throw new Error('The teacher profile response is invalid.');
      }

      setTeacher(teacherData);

      const ctId =
        teacherData.class_teacher_class_id ||
        teacherData.current_class_id ||
        teacherData.currentClass?.id ||
        null;
      setClassTeacherId(ctId);

      const classesRes = await apiClient.get('/teachers/me/classes');
      const rawClassData = classesRes.data?.data ?? classesRes.data ?? [];
      const classItems = Array.isArray(rawClassData)
        ? rawClassData
        : Array.isArray(rawClassData.rows)
          ? rawClassData.rows
          : Array.isArray(rawClassData.classes)
            ? rawClassData.classes
            : [];

      let totalStudents = 0;
      const studentsByClass = {};
      for (const cls of classItems) {
        try {
          const res = await apiClient.get(`/classes/${cls.id}/students`, {
            params: { limit: 500 },
          });
          let students = res.data?.data || res.data || [];
          if (students?.rows) students = students.rows;
          if (!Array.isArray(students)) students = [];

          studentsByClass[cls.id] = students;
          cls.studentCount = students.length;
          totalStudents += students.length;
        } catch (err) {
          console.error(`Failed to load students for class ${cls.id}:`, err);
          studentsByClass[cls.id] = [];
          cls.studentCount = 0;
        }
      }
      setClasses(classItems);
      setStudentCount(totalStudents);

      const activities = [];
      if (classItems.length > 0) {
        if (ctId) {
          const homeroomClass = classItems.find(
            (currentClass) => Number(currentClass.id) === Number(ctId)
          );
          if (homeroomClass) {
            activities.push({
              id: 'class-teacher',
              user: 'System',
              action: `You are the Class Teacher for ${homeroomClass.name}`,
              time: new Date().toISOString(),
              type: 'info',
              icon: AcademicCapIcon,
              iconColor: 'text-yellow-600',
              bgColor: 'bg-yellow-50',
            });
          }
        }
        activities.push({
          id: 'class-info',
          user: 'System',
          action: `You are teaching ${classItems.length} class(es) in total`,
          time: new Date().toISOString(),
          type: 'info',
          icon: BookOpenIcon,
          iconColor: 'text-blue-500',
          bgColor: 'bg-blue-50',
        });
        classItems.slice(0, 3).forEach(cls => {
          activities.push({
            id: `class-${cls.id}`,
            user: cls.name,
            action: `${cls.level || 'Class'} · ${cls.faculty || 'General'}`,
            time: new Date().toISOString(),
            type: 'class',
            icon: AcademicCapIcon,
            iconColor: 'text-purple-500',
            bgColor: 'bg-purple-50',
          });
        });
      }
      setRecentActivities(activities);

      await fetchAttendanceSummary(classItems, studentsByClass);
      await fetchCurrentTermAttendance(classItems);
    } catch (error) {
      console.error('Error fetching teacher data:', error);
      const message =
        error.response?.data?.message ||
        error.message ||
        'Failed to load teacher dashboard';

      if (!options.background) toast.error(message);
    } finally {
      if (!options.background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeacherDataRef.current = fetchTeacherData;
  }, [fetchTeacherData]);

  useEffect(() => {
    fetchTeacherData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Socket listeners
  useEffect(() => {
    const handleUpdate = (event) => {
      console.log('📡 Teacher dashboard update received:', event);
      if (fetchTeacherDataRef.current) {
        fetchTeacherDataRef.current({ background: true });
      }
    };

    socket.on('student:updated', handleUpdate);
    socket.on('teacher:updated', handleUpdate);
    socket.on('class:updated', handleUpdate);
    socket.on('attendance:updated', handleUpdate);

    return () => {
      socket.off('student:updated', handleUpdate);
      socket.off('teacher:updated', handleUpdate);
      socket.off('class:updated', handleUpdate);
      socket.off('attendance:updated', handleUpdate);
    };
  }, []);

  const fetchAttendanceSummary = async (classItems, studentsByClass) => {
    try {
      setChartLoading(true);

      const emptySummary = {
        morning: {
          boysPresent: 0,
          boysAbsent: 0,
          girlsPresent: 0,
          girlsAbsent: 0,
        },
        afternoon: {
          boysPresent: 0,
          boysAbsent: 0,
          girlsPresent: 0,
          girlsAbsent: 0,
        },
      };

      if (!Array.isArray(classItems) || classItems.length === 0) {
        setAttendanceSummary(emptySummary);
        setAttendanceStats({ present: 0, absent: 0, total: 0 });
        return;
      }

      const today = new Date().toISOString().split('T')[0];
      const sessions = ['morning', 'afternoon'];

      const requests = classItems.flatMap((cls) =>
        sessions.map(async (session) => {
          try {
            const response = await apiClient.get(`/attendance/date/${today}`, {
              params: {
                classId: cls.id,
                session,
              },
            });

            const records = response.data?.data || [];

            return {
              classId: cls.id,
              session,
              records: Array.isArray(records) ? records : [],
            };
          } catch (error) {
            console.error(
              `Failed to load ${session} attendance for class ${cls.id}:`,
              error
            );

            return {
              classId: cls.id,
              session,
              records: [],
            };
          }
        })
      );

      const attendanceResults = await Promise.all(requests);
      let present = 0;
      let absent = 0;
      let total = 0;

      attendanceResults.forEach(({ classId, session, records }) => {
        const students = studentsByClass[classId] || [];
        const studentMap = new Map(
          students.map((student) => [Number(student.id), student])
        );

        records.forEach((record) => {
          const status = String(record.status || '').toLowerCase();
          const student = studentMap.get(Number(record.student_id));
          const gender = String(
            student?.gender || record?.student?.gender || ''
          ).toLowerCase();

          if (status === 'present') present += 1;
          if (status === 'absent') absent += 1;
          if (status) total += 1;

          const isBoy = ['male', 'boy', 'm'].includes(gender);
          const isGirl = ['female', 'girl', 'f'].includes(gender);

          if (status === 'present' && isBoy) {
            emptySummary[session].boysPresent += 1;
          } else if (status === 'absent' && isBoy) {
            emptySummary[session].boysAbsent += 1;
          } else if (status === 'present' && isGirl) {
            emptySummary[session].girlsPresent += 1;
          } else if (status === 'absent' && isGirl) {
            emptySummary[session].girlsAbsent += 1;
          }
        });
      });

      setAttendanceSummary(emptySummary);
      setAttendanceStats({ present, absent, total });
    } catch (error) {
      console.error('Error fetching attendance summary:', error);
      toast.error('Failed to load attendance chart');
      setAttendanceSummary(null);
      setAttendanceStats({ present: 0, absent: 0, total: 0 });
    } finally {
      setChartLoading(false);
    }
  };

  const fetchCurrentTermAttendance = async (classItems) => {
    try {
      if (!Array.isArray(classItems) || classItems.length === 0) {
        setCurrentTermStats({
          name: 'Current Term',
          percentage: 0,
          attendedSessions: 0,
          possibleSessions: 0,
        });
        return;
      }

      const termsResponse = await apiClient.get('/attendance/terms');
      const termsValue = termsResponse.data?.data || termsResponse.data || [];
      const terms = Array.isArray(termsValue)
        ? termsValue
        : termsValue?.rows || termsValue?.terms || [];
      const today = new Date().toISOString().split('T')[0];
      const currentTerm = terms.find(
        (term) => term.start_date && term.end_date &&
          today >= term.start_date && today <= term.end_date
      ) || terms[terms.length - 1];

      if (!currentTerm) return;

      const reports = await Promise.all(
        classItems.map(async (classItem) => {
          try {
            const response = await apiClient.get(
              `/attendance/report/class/${classItem.id}`,
              { params: { termId: currentTerm.id } }
            );
            return response.data?.data?.register || null;
          } catch (error) {
            console.error(`Failed to load term report for class ${classItem.id}:`, error);
            return null;
          }
        })
      );

      const totals = reports.filter(Boolean).reduce(
        (result, register) => ({
          attendedSessions: result.attendedSessions + Number(register.actualAttendances || 0),
          possibleSessions: result.possibleSessions + Number(register.possibleAttendances || 0),
        }),
        { attendedSessions: 0, possibleSessions: 0 }
      );

      setCurrentTermStats({
        name: currentTerm.name || `Term ${currentTerm.term_number || ''}`.trim(),
        ...totals,
        percentage: totals.possibleSessions > 0
          ? Number(((totals.attendedSessions / totals.possibleSessions) * 100).toFixed(2))
          : 0,
      });
    } catch (error) {
      console.error('Failed to load current-term attendance:', error);
    }
  };

  const timeAgo = (date) => {
    if (!date) return 'Just now';
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return past.toLocaleDateString();
  };

  if (loading) return <FireLoader fullScreen size="lg" text="Loading..." />;

  if (!teacher) {
    return (
      <div className="text-center py-12">
        <p className="text-primary-500">No teacher profile found for your account.</p>
        <button
          onClick={() => navigate('/app/dashboard')}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-xl"
        >
          Go Back
        </button>
      </div>
    );
  }

  const fullName = `${teacher.first_name} ${teacher.last_name}`;

  const chartData = attendanceSummary
    ? [
        {
          name: 'Morning',
          'Boys Present': attendanceSummary.morning.boysPresent,
          'Boys Absent': attendanceSummary.morning.boysAbsent,
          'Girls Present': attendanceSummary.morning.girlsPresent,
          'Girls Absent': attendanceSummary.morning.girlsAbsent,
        },
        {
          name: 'Afternoon',
          'Boys Present': attendanceSummary.afternoon.boysPresent,
          'Boys Absent': attendanceSummary.afternoon.boysAbsent,
          'Girls Present': attendanceSummary.afternoon.girlsPresent,
          'Girls Absent': attendanceSummary.afternoon.girlsAbsent,
        },
      ]
    : [];

  const totalMorning = attendanceSummary
    ? attendanceSummary.morning.boysPresent + attendanceSummary.morning.boysAbsent +
      attendanceSummary.morning.girlsPresent + attendanceSummary.morning.girlsAbsent
    : 0;
  const totalAfternoon = attendanceSummary
    ? attendanceSummary.afternoon.boysPresent + attendanceSummary.afternoon.boysAbsent +
      attendanceSummary.afternoon.girlsPresent + attendanceSummary.afternoon.girlsAbsent
    : 0;
  const totalPresent = attendanceSummary
    ? attendanceSummary.morning.boysPresent + attendanceSummary.morning.girlsPresent +
      attendanceSummary.afternoon.boysPresent + attendanceSummary.afternoon.girlsPresent
    : 0;
  const totalAbsent = attendanceSummary
    ? attendanceSummary.morning.boysAbsent + attendanceSummary.morning.girlsAbsent +
      attendanceSummary.afternoon.boysAbsent + attendanceSummary.afternoon.girlsAbsent
    : 0;

  const handleMarkAttendance = () => {
    if (classes.length === 0) {
      toast.error('You are not assigned to any class.');
      return;
    }
    if (classes.length === 1) {
      navigate(`/app/classes/${classes[0].id}/attendance/mark`);
    } else {
      navigate('/app/classes');
    }
  };

  return (
    <>
      <style>{`
        @keyframes wave {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(15deg); }
          50% { transform: rotate(0deg); }
          75% { transform: rotate(-15deg); }
          100% { transform: rotate(0deg); }
        }
        .wave-emoji {
          display: inline-block;
          animation: wave 1.5s ease-in-out infinite;
          transform-origin: 70% 70%;
        }
        .responsive-padding {
          padding-left: 0.75rem;
          padding-right: 0.75rem;
        }
        @media (min-width: 640px) {
          .responsive-padding {
            padding-left: 1.5rem;
            padding-right: 1.5rem;
          }
        }
        @media (min-width: 1024px) {
          .responsive-padding {
            padding-left: 2rem;
            padding-right: 2rem;
          }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6 sm:space-y-8 responsive-padding">
        {/* ========== WELCOME HEADER ========== */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary-600 to-secondary-600 shadow-lg p-5 sm:p-8 text-white">
          <div className="absolute right-0 top-0 opacity-10">
            <AcademicCapIcon className="w-32 sm:w-48 h-32 sm:h-48" />
          </div>
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-3xl font-bold leading-tight">
                Welcome, {fullName}
                <span className="wave-emoji ml-2" style={{ fontSize: 'inherit' }}>👋</span>
              </h1>
              <p className="text-sm sm:text-base text-white/80 mt-1">
                Let's see what's going on in your class
              </p>
            </div>
          </div>
        </div>

        {/* Quick Action Tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <div
            onClick={handleMarkAttendance}
            className="bg-white rounded-xl p-3 sm:p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors border border-gray-100 hover:border-blue-300 touch-manipulation"
          >
            <CalendarDaysIcon className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600 mx-auto mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-gray-700">Mark Attendance</span>
          </div>
          <div
            onClick={() => navigate('/app/classes')}
            className="bg-white rounded-xl p-3 sm:p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors border border-gray-100 hover:border-purple-300 touch-manipulation"
          >
            <BookOpenIcon className="w-7 h-7 sm:w-8 sm:h-8 text-purple-600 mx-auto mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-gray-700">My Classes</span>
          </div>
          <div
            onClick={() => navigate('/app/students')}
            className="bg-white rounded-xl p-3 sm:p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors border border-gray-100 hover:border-green-300 touch-manipulation"
          >
            <UserGroupIcon className="w-7 h-7 sm:w-8 sm:h-8 text-green-600 mx-auto mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-gray-700">Students</span>
          </div>
          <div
            onClick={() => navigate('/app/library')}
            className="bg-white rounded-xl p-3 sm:p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors border border-gray-100 hover:border-orange-300 touch-manipulation"
          >
            <BookOpenIcon className="w-7 h-7 sm:w-8 sm:h-8 text-orange-600 mx-auto mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-gray-700">Library</span>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-blue-200 transition-colors cursor-pointer group" onClick={() => navigate('/app/classes')}>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors">
                <BookOpenIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">Total Classes</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">{classes.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-green-200 transition-colors cursor-pointer group" onClick={() => navigate('/app/students')}>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-green-50 text-green-600 group-hover:bg-green-100 transition-colors">
                <UserGroupIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">Students</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">{studentCount}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-purple-200 transition-colors">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-purple-50 text-purple-600 group-hover:bg-purple-100 transition-colors">
                <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">Present Today</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">{attendanceStats.present}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 sm:p-5 border border-gray-100 hover:border-orange-200 transition-colors">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-orange-50 text-orange-600 group-hover:bg-orange-100 transition-colors">
                <BriefcaseIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-sm font-medium text-gray-400 uppercase tracking-wider">{currentTermStats.name}</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-800 mt-0.5">{currentTermStats.percentage.toFixed(2)}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Two‑column layout: Classes + Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Classes – 2/3 width */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 h-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">Your Classes</h3>
                <button
                  onClick={() => navigate('/app/classes')}
                  className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                >
                  View All <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </div>
              {classes.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400">No classes assigned yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {classes.map((cls) => {
                    const isHomeroom = classTeacherId === cls.id;
                    return (
                      <div
                        key={cls.id}
                        className={`group p-3 sm:p-4 rounded-xl transition-colors cursor-pointer border ${
                          isHomeroom
                            ? 'bg-yellow-50 border-yellow-300 hover:bg-yellow-100'
                            : 'bg-gray-50 border-transparent hover:bg-gray-100 hover:border-primary-200'
                        }`}
                        onClick={() => navigate(`/app/classes/${cls.id}`)}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                              <p className="font-semibold text-gray-800 group-hover:text-primary-700 transition-colors text-sm sm:text-base truncate">
                                {cls.name}
                              </p>
                              {isHomeroom && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-200 text-yellow-800 whitespace-nowrap">
                                  Class Teacher
                                </span>
                              )}
                            </div>
                            <p className="text-xs sm:text-sm text-gray-500 capitalize truncate">{cls.level} · {cls.faculty}</p>
                            <div className="mt-1 sm:mt-2 flex items-center gap-2">
                              <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                                {cls.studentCount || 0} students
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-row sm:flex-col gap-1 flex-shrink-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/app/classes/${cls.id}/attendance/mark`);
                              }}
                              className="text-xs px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors touch-manipulation"
                            >
                              Attendance
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/app/students?class=${cls.id}`);
                              }}
                              className="text-xs px-2 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors touch-manipulation"
                            >
                              Students
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Activity – 1/3 width */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 h-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">Activity</h3>
                <button
                  onClick={() => navigate('/app/notifications')}
                  className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  View All
                </button>
              </div>
              <div className="space-y-4">
                {recentActivities.length > 0 ? (
                  recentActivities.slice(0, 5).map((activity) => (
                    <div key={activity.id} className="flex items-start gap-3">
                      <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full ${activity.bgColor || 'bg-gray-100'} flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1`}>
                        <activity.icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${activity.iconColor || 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm text-gray-700 break-words">
                          <span className="font-medium">{activity.user}</span> {activity.action}
                        </p>
                        <p className="text-xs text-gray-400">{timeAgo(activity.time)}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <ClockIcon className="w-8 h-8 sm:w-10 sm:h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Attendance Summary Chart */}
        <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-2">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-800">Attendance Summary</h3>
              <p className="text-xs sm:text-sm text-gray-500">Present / Absent breakdown by gender and session</p>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3 text-xs sm:text-sm">
              <div className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500"></span>
                <span className="text-gray-600">Present</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500"></span>
                <span className="text-gray-600">Absent</span>
              </div>
            </div>
          </div>

          {chartLoading ? (
            <div className="h-48 sm:h-64 flex items-center justify-center">
              <div className="w-7 h-7 sm:w-8 sm:h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : attendanceSummary ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="bg-green-50 rounded-xl p-2 sm:p-3 text-center">
                  <p className="text-[10px] sm:text-xs text-gray-500">Total Present</p>
                  <p className="text-base sm:text-xl font-bold text-green-700">{totalPresent}</p>
                </div>
                <div className="bg-red-50 rounded-xl p-2 sm:p-3 text-center">
                  <p className="text-[10px] sm:text-xs text-gray-500">Total Absent</p>
                  <p className="text-base sm:text-xl font-bold text-red-700">{totalAbsent}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-2 sm:p-3 text-center">
                  <p className="text-[10px] sm:text-xs text-gray-500">Morning Total</p>
                  <p className="text-base sm:text-xl font-bold text-blue-700">{totalMorning}</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-2 sm:p-3 text-center">
                  <p className="text-[10px] sm:text-xs text-gray-500">Afternoon Total</p>
                  <p className="text-base sm:text-xl font-bold text-purple-700">{totalAfternoon}</p>
                </div>
              </div>

              <div className="w-full h-52 sm:h-64 md:h-72 lg:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                    barCategoryGap="15%"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        borderRadius: '0.5rem',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                        fontSize: '12px',
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="Boys Present" stackId="boys" fill="#10b981" />
                    <Bar dataKey="Boys Absent" stackId="boys" fill="#ef4444" />
                    <Bar dataKey="Girls Present" stackId="girls" fill="#3b82f6" />
                    <Bar dataKey="Girls Absent" stackId="girls" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-3 sm:mt-4 text-[10px] sm:text-xs text-gray-400 text-center">
                * Data reflects today’s attendance for all your classes (morning and afternoon sessions)
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No attendance data available for today.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}